import React, { useState, useEffect } from 'react';
import { fetchAvailableModels } from '../services/api';
import { getCatalogResolutions, getResolutionCreditsForModel, getToolkitResolutions, setToolkitResolution } from '../utils/videoModelResolutions';

// Resolution chips on a toolkit tile: catalog options, catalog default marked, user pick highlighted.
const ResolutionChips = ({ model, modelId, picked, onPick }) => {
  const options = getCatalogResolutions(model);
  if (options.length === 0) {
    return model.default_resolution
      ? <p className="text-[11px] text-gray-500 mt-2" data-testid={`model-default-res-${modelId}`}>Resolution: {model.default_resolution}</p>
      : null;
  }
  const credits = getResolutionCreditsForModel(model);
  const def = options.includes(model.default_resolution) ? model.default_resolution : options[0];
  const active = picked && options.includes(picked) ? picked : def;
  return (
    <div className="mt-2.5" data-testid={`model-resolutions-${modelId}`}>
      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Resolution</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(r => (
          <button key={r} type="button"
            onClick={(e) => { e.stopPropagation(); onPick(r === def ? null : r); }}
            className={`px-2 py-1 rounded-md text-[11px] border transition-colors ${r === active
              ? 'bg-purple-600 border-purple-500 text-white'
              : 'bg-gray-900/60 border-gray-700 text-gray-300 hover:border-purple-500/60'}`}
            data-testid={`model-res-${modelId}-${r}`}>
            {r}{credits[r] != null ? ` · ${credits[r]} cr/s` : ''}{r === def ? ' (default)' : ''}
          </button>
        ))}
      </div>
    </div>
  );
};

// Same-tab custom event so other components (Storyboard) can re-stamp
// shot.preProduction.selectedVideoModel when the user changes model selection.
// Browser `storage` events only fire across tabs — we need a same-tab signal.
const persistSelectedModels = (models) => {
  localStorage.setItem('selectedAIModels', JSON.stringify(models));
  try { window.dispatchEvent(new CustomEvent('ai-models-changed', { detail: models })); }
  catch { /* noop */ }
};

/**
 * AI Toolkit Selector Component
 * Two-panel layout: capability list on left, model selection on right
 */
export default function AIToolkitSelector({ onContinue, onBack }) {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCap, setActiveCap] = useState(null);
  const [selectedModels, setSelectedModels] = useState(() => {
    try {
      const stored = localStorage.getItem('selectedAIModels');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const [resolutionPicks, setResolutionPicks] = useState(() => getToolkitResolutions());
  const pickResolution = (capKey, modelId, res) => {
    setToolkitResolution(modelId, res);
    setResolutionPicks(getToolkitResolutions());
    if (selectedModels[capKey] !== modelId) handleModelSelect(capKey, modelId);
  };

  useEffect(() => { loadCapabilities(); }, []);

  const loadCapabilities = async () => {
    try {
      setLoading(true); setError(null);
      const caps = await fetchAvailableModels();
      setCapabilities(caps);
      if (caps.length > 0) setActiveCap(caps[0].key);

      // Persist tier data for fallback ordering in aiService
      const tierData = {};
      for (const cap of caps) {
        if (cap.tiers && typeof cap.tiers === 'object') {
          tierData[cap.key] = cap.tiers;
        }
      }
      localStorage.setItem('aiCapabilityTiers', JSON.stringify(tierData));

      setSelectedModels(prev => {
        const updated = { ...prev };
        for (const cap of caps) {
          // Only set default if nothing is stored — default to first Premium model
          if (!updated[cap.key] && cap.models?.length > 0) {
            const premiumModels = cap.tiers?.premium || [];
            if (premiumModels.length > 0) {
              updated[cap.key] = premiumModels[0].key || premiumModels[0].id;
            } else {
              updated[cap.key] = cap.models[0].key || cap.models[0].id;
            }
          }
        }
        persistSelectedModels(updated);
        return updated;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (capKey, modelKey) => {
    // Feb 2026 — Diagnostic log so we can catch admin-catalog UUID
    // collisions or mislabelled tiles at pick-time. Prints the exact
    // display name the user saw AND the id being persisted. If those
    // two ever disagree later in the pipeline we know the admin catalog
    // is out of sync.
    try {
        const cap = capabilities.find(c => c.key === capKey);
        const clickedModel = cap ? getDisplayModels(cap).find(m => (m.key || m.id) === modelKey) : null;
        console.log('🧰 [AI-Toolkit] handleModelSelect', {
            capability: capKey,
            persistedId: modelKey,
            displayName: clickedModel?.name || '(unknown)',
            provider: clickedModel?.provider || '(none)',
        });
    } catch { /* noop */ }

    setSelectedModels(prev => {
      // Feb 13 2026 — Toggle behaviour changed. Previously clicking the
      // currently-selected tile UN-selected it (deleted the key), leaving
      // the capability with NO explicit pick — which caused the backend to
      // fall back to its own hidden default (e.g. `gemini-3-pro-image-preview`
      // for text-to-image). Now clicking the selected tile "resets" to the
      // top-priority model of the currently-active tier — the capability
      // ALWAYS has a checked model. This matches the AI Settings promise
      // that a highest-priority default is always applied.
      const updated = { ...prev };
      if (prev[capKey] === modelKey) {
        const cap = capabilities.find(c => c.key === capKey);
        const topId = cap ? getHighestPriorityModelId(cap) : null;
        if (topId && topId !== modelKey) {
          updated[capKey] = topId;
        } else {
          // The user clicked the top-priority tile itself — keep it
          // selected. No-op that still guarantees a persisted default.
          updated[capKey] = modelKey;
        }
      } else {
        updated[capKey] = modelKey;
      }
      persistSelectedModels(updated);
      return updated;
    });
  };

  const handleContinue = () => {
    // Enforce: if no model selected for a capability, default to Premium
    const finalModels = { ...selectedModels };
    for (const cap of capabilities) {
      if (!finalModels[cap.key]) {
        const premiumModels = cap.tiers?.premium || [];
        if (premiumModels.length > 0) {
          finalModels[cap.key] = premiumModels[0].key || premiumModels[0].id;
        } else if (cap.models?.length > 0) {
          finalModels[cap.key] = cap.models[0].key || cap.models[0].id;
        }
      }
    }
    setSelectedModels(finalModels);
    persistSelectedModels(finalModels);
    if (onContinue) onContinue(finalModels);
  };

  const activeCapData = capabilities.find(c => c.key === activeCap);

  // Model pricing tiers (within each capability) — from backend `tiers` dict
  const PRICING_TIERS = ['premium', 'standard', 'economy'];
  const TIER_LABELS = { premium: 'Premium', standard: 'Standard', economy: 'Economy' };
  const [activeModelTier, setActiveModelTier] = useState({}); // { capKey: 'premium' | 'standard' | 'economy' }

  // Get available pricing tiers for a capability (only tiers with models)
  const getAvailablePricingTiers = (cap) => {
    if (!cap?.tiers || typeof cap.tiers !== 'object') return [];
    return PRICING_TIERS.filter(t => cap.tiers[t]?.length > 0);
  };

  // Get active pricing tier for a capability
  const getActivePricingTier = (cap) => {
    if (!cap) return null;
    const available = getAvailablePricingTiers(cap);
    if (available.length === 0) return null;
    const stored = activeModelTier[cap.key];
    if (stored && available.includes(stored)) return stored;
    return available[0]; // Default: first available (premium > standard > economy)
  };

  // Get models to display for active capability + selected pricing tier
  const getDisplayModels = (cap) => {
    if (!cap) return [];
    const available = getAvailablePricingTiers(cap);
    // If no tiers, show flat model list
    if (available.length === 0) return cap.models || [];
    const activeTier = getActivePricingTier(cap);
    if (activeTier && cap.tiers?.[activeTier]) return cap.tiers[activeTier];
    return cap.models || [];
  };

  // Feb 13 2026 — Compute the "highest priority" model for a capability
  // considering the active tier. If tiers exist, use tier[0]. Otherwise the
  // catalog's models[0]. Returns model key/id or null. Single source of
  // truth for both auto-check-on-load AND tier-switch defaulting.
  const getHighestPriorityModelId = (cap) => {
    if (!cap) return null;
    const displayed = getDisplayModels(cap);
    if (!displayed || displayed.length === 0) return null;
    const top = displayed[0];
    return top?.key || top?.id || null;
  };

  // Feb 13 2026 — Auto-check the highest-priority model whenever:
  //   1. The active capability changes (user clicks a different capability),
  //   2. The active tier changes (user clicks Premium / Standard / Economy),
  //   3. Capabilities are first loaded.
  //
  // Behaviour: If the currently-selected model for this capability is EITHER
  // empty OR not present in the active tier's display list, auto-select the
  // top-priority model of the active tier. This guarantees the UI ALWAYS
  // shows a visible checkmark AND the localStorage.selectedAIModels entry
  // is always populated — so the request body never falls back to
  // model_key: 'default'.
  useEffect(() => {
    if (!activeCapData) return;
    const displayed = getDisplayModels(activeCapData);
    if (!displayed || displayed.length === 0) return;
    const displayedIds = displayed.map(m => m.key || m.id);
    const currentPick = selectedModels[activeCapData.key];
    // If current pick is missing OR belongs to a different tier, refresh
    // it to the top-priority of the current tier.
    if (!currentPick || !displayedIds.includes(currentPick)) {
      const topId = getHighestPriorityModelId(activeCapData);
      if (topId) {
        setSelectedModels(prev => {
          if (prev[activeCapData.key] === topId) return prev;
          const updated = { ...prev, [activeCapData.key]: topId };
          persistSelectedModels(updated);
          return updated;
        });
      }
    }
  }, [activeCap, activeCapData, activeModelTier[activeCapData?.key], capabilities.length]);

  // Pricing tier styling
  const pricingTierStyle = {
    premium: { bg: 'bg-amber-500/10', border: 'border-amber-500/50', text: 'text-amber-400', activeBg: 'bg-amber-500/20', ring: 'ring-amber-500/30' },
    standard: { bg: 'bg-blue-500/10', border: 'border-blue-500/50', text: 'text-blue-400', activeBg: 'bg-blue-500/20', ring: 'ring-blue-500/30' },
    economy: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/50', text: 'text-emerald-400', activeBg: 'bg-emerald-500/20', ring: 'ring-emerald-500/30' },
  };

  // Group capabilities by logical category (override backend task_type where needed)
  const CAPABILITY_GROUP_OVERRIDES = {
    'start-end-frame videos': 'Video Generation',
    'Text to video': 'Video Generation',
    'Image to video': 'Video Generation',
    'video_editing': 'Video Generation',
    'motion_control': 'Video Generation',
    // References-to-video — added Feb 2026. Backend may key this several ways;
    // accept all common aliases so the panel-based shot model picker finds it
    // under "Video Generation" regardless of which alias the admin chose.
    'References to video': 'Video Generation',
    'references-to-video': 'Video Generation',
    'references_to_video': 'Video Generation',
    'reference-to-video': 'Video Generation',
    'reference_to_video': 'Video Generation',
    'multi-frame-video': 'Video Generation',
    'keyframes-to-video': 'Video Generation',
    'Text to image': 'Image Generation',
    'Image reference to image': 'Image Generation',
  };
  const TASK_TYPE_LABELS = {
    'image_generation': 'Image Generation',
    'video_generation': 'Video Generation',
    'prompt_generation': 'AI Prompts',
    'script_generation': 'Script & Story',
    'character_generation': 'Characters',
    'audio_transcription': 'Audio & Voice',
    'voice_change': 'Audio & Voice',
    'speech_to_speech': 'Audio & Voice',
    'reasoning': 'AI Reasoning',
    'other': 'Other',
  };
  const getCapabilityGroup = (cap) => {
    // Check name/key override first
    if (CAPABILITY_GROUP_OVERRIDES[cap.name]) return CAPABILITY_GROUP_OVERRIDES[cap.name];
    if (CAPABILITY_GROUP_OVERRIDES[cap.key]) return CAPABILITY_GROUP_OVERRIDES[cap.key];
    // Resilient auto-detection: if name OR key clearly references "video"
    // (case-insensitive), group it under Video Generation rather than "Other".
    // This way future backend additions like "References to video" automatically
    // land in the right section without needing a code change here.
    const probe = `${cap.name || ''} ${cap.key || ''}`.toLowerCase();
    if (/\bvideo\b/.test(probe)) return 'Video Generation';
    if (/\bimage\b/.test(probe)) return 'Image Generation';
    // Fall back to task_type mapping
    return TASK_TYPE_LABELS[cap.task_type] || 'Other';
  };
  const groupedByTier = capabilities.reduce((acc, cap) => {
    const label = getCapabilityGroup(cap);
    if (!acc[label]) acc[label] = [];
    acc[label].push(cap);
    return acc;
  }, {});

  const getTierColor = (tier) => {
    const tierColors = {
      'Image Generation': 'text-purple-400 border-purple-500/30',
      'Video Generation': 'text-blue-400 border-blue-500/30',
      'Script & Story': 'text-emerald-400 border-emerald-500/30',
      'Audio & Voice': 'text-amber-400 border-amber-500/30',
      'AI Prompts': 'text-cyan-400 border-cyan-500/30',
      'Characters': 'text-pink-400 border-pink-500/30',
      'AI Reasoning': 'text-indigo-400 border-indigo-500/30',
      'Other': 'text-gray-400 border-gray-600/30',
    };
    return tierColors[tier] || tierColors['Other'];
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading AI models...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={loadCapabilities} className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden" data-testid="ai-toolkit-selector">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors" data-testid="toolkit-back-btn">
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              Back
            </button>
          )}
          <div>
            <h1 className="text-lg font-bold text-white">Select Your AI Toolkit</h1>
            <p className="text-xs text-gray-500">Choose preferred models for each capability. Highest-priority is auto-selected. Click a different tile to override.</p>
          </div>
        </div>
        <button onClick={handleContinue} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 transition-colors" data-testid="toolkit-continue-btn">
          Continue
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
        </button>
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Capability list grouped by tier */}
        <div className="w-[280px] border-r border-gray-800 overflow-y-auto shrink-0 bg-[#0c0c14]" data-testid="capability-list">
          {Object.entries(groupedByTier).map(([tier, caps]) => (
            <div key={tier}>
              {/* Tier header */}
              <div className={`sticky top-0 z-10 px-4 py-2 bg-[#0a0a12] border-b ${getTierColor(tier).split(' ')[1] || 'border-gray-700/40'}`}>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${getTierColor(tier).split(' ')[0]}`}>
                  {tier}
                </span>
              </div>
              {/* Capabilities in this tier */}
              {caps.map((cap) => {
                const isActive = activeCap === cap.key;
                const selectedModelName = cap.models?.find(m => m.key === selectedModels[cap.key])?.name;
                return (
                  <button key={cap.key} onClick={() => setActiveCap(cap.key)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-800/60 transition-colors ${isActive ? 'bg-purple-600/10 border-l-2 border-l-purple-500' : 'hover:bg-gray-800/40 border-l-2 border-l-transparent'}`}
                    data-testid={`cap-${cap.key}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium truncate ${isActive ? 'text-purple-300' : 'text-gray-300'}`}>{cap.name}</span>
                      <span className="text-[10px] text-gray-600 ml-2 shrink-0">{cap.models?.length || 0}</span>
                    </div>
                    {selectedModelName ? (
                      <p className="text-[10px] text-gray-500 mt-0.5 truncate">{selectedModelName}</p>
                    ) : (
                      <p className="text-[10px] text-amber-500/70 mt-0.5 truncate">
                        {getAvailablePricingTiers(cap).length > 0
                          ? 'Default: Premium'
                          : `Default: ${cap.models?.[0]?.name || 'first available'}`}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Right: Models for active capability */}
        <div className="flex-1 overflow-y-auto p-6" data-testid="model-panel">
          {activeCapData ? (
            <>
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-base font-bold text-white">{activeCapData.name}</h2>
                  {activeCapData.task_type && (
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getTierColor(getCapabilityGroup(activeCapData))}`}>
                      {getCapabilityGroup(activeCapData)}
                    </span>
                  )}
                </div>
                {activeCapData.description && <p className="text-xs text-gray-500 mt-1">{activeCapData.description}</p>}
              </div>

              {/* Pricing tier tabs (only if capability has tiers) */}
              {getAvailablePricingTiers(activeCapData).length > 0 && (
                <div className="flex gap-2 mb-4" data-testid="pricing-tier-tabs">
                  {getAvailablePricingTiers(activeCapData).map(tier => {
                    const style = pricingTierStyle[tier];
                    const isActive = getActivePricingTier(activeCapData) === tier;
                    const tierModels = activeCapData.tiers?.[tier] || [];
                    return (
                      <button key={tier}
                        onClick={() => setActiveModelTier(prev => ({ ...prev, [activeCapData.key]: tier }))}
                        className={`px-4 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                          isActive
                            ? `${style.activeBg} ${style.border} ${style.text} ring-1 ring-inset ${style.ring}`
                            : `bg-gray-800/40 border-gray-700/60 text-gray-500 hover:text-gray-300 hover:border-gray-600`
                        }`}
                        data-testid={`pricing-tier-${tier}`}
                      >
                        {TIER_LABELS[tier]}
                        <span className="ml-1.5 opacity-60">({tierModels.length})</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {getDisplayModels(activeCapData).map((model) => {
                  const modelId = model.key || model.id;
                  const isSelected = selectedModels[activeCapData.key] === modelId;
                  return (
                    <div key={modelId} role="button" tabIndex={0} onClick={() => handleModelSelect(activeCapData.key, modelId)}
                      onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleModelSelect(activeCapData.key, modelId); } }}
                      className={`relative text-left p-4 rounded-xl border cursor-pointer transition-all ${isSelected
                        ? 'border-purple-500 bg-purple-600/10 ring-1 ring-purple-500/30'
                        : 'border-gray-700/60 bg-gray-800/30 hover:border-gray-600 hover:bg-gray-800/50'}`}
                      data-testid={`model-${modelId}`}>
                      {isSelected && (
                        <div className="absolute top-2.5 right-2.5 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center" title="Click to deselect">
                          <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                        </div>
                      )}
                      <p className={`text-sm font-semibold ${isSelected ? 'text-purple-300' : 'text-gray-200'}`}>{model.name}</p>
                      {model.provider && <p className="text-[11px] text-gray-500 mt-0.5">{model.provider}</p>}
                      {model.description && <p className="text-[11px] text-gray-600 mt-1 line-clamp-2">{model.description}</p>}
                      <p className={`text-xs mt-2 font-medium ${isSelected ? 'text-purple-400' : 'text-emerald-500'}`}>{model.cost_estimate || 'Included'}</p>
                      <ResolutionChips model={model} modelId={modelId} picked={resolutionPicks[modelId]}
                        onPick={(r) => pickResolution(activeCapData.key, modelId, r)} />
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Select a capability from the left panel.</p>
          )}
        </div>
      </div>
    </div>
  );
}
